/**
 * Normalized State Utilities
 * Helper functions for managing normalized Redux state
 */
import { NormalizedState } from '../types/api';

export type { NormalizedState };

/**
 * Normalize an array of entities into a normalized state structure
 */
export function normalizeEntities<T extends { id: string }>(
  items: T[]
): { entities: Record<string, T>; ids: string[] } {
  const entities: Record<string, T> = {};
  const ids: string[] = [];

  items.forEach((item) => {
    if (item.id) {
      entities[item.id] = item;
      ids.push(item.id);
    }
  });

  return { entities, ids };
}

/**
 * Denormalize normalized state back to array
 */
export function denormalizeEntities<T>(
  normalizedState: { entities: Record<string, T>; ids: string[] }
): T[] {
  if (!normalizedState || !normalizedState.ids || !Array.isArray(normalizedState.ids)) {
    return [];
  }
  return normalizedState.ids
    .map((id) => normalizedState.entities[id])
    .filter((entity): entity is T => entity !== undefined && entity !== null);
}

/**
 * Update entity in normalized state
 */
export function updateEntity<T extends { id: string }>(
  state: { entities: Record<string, T>; ids: string[] },
  entity: T
): { entities: Record<string, T>; ids: string[] } {
  return {
    ...state,
    entities: {
      ...state.entities,
      [entity.id]: entity,
    },
  };
}

/**
 * Remove entity from normalized state
 */
export function removeEntity<T>(
  state: { entities: Record<string, T>; ids: string[] },
  id: string
): { entities: Record<string, T>; ids: string[] } {
  const { [id]: removed, ...entities } = state.entities;
  return {
    entities,
    ids: state.ids.filter((entityId) => entityId !== id),
  };
}

/**
 * Add entity to normalized state
 */
export function addEntity<T extends { id: string }>(
  state: { entities: Record<string, T>; ids: string[] },
  entity: T
): { entities: Record<string, T>; ids: string[] } {
  if (state.entities[entity.id]) {
    return updateEntity(state, entity);
  }

  return {
    entities: {
      ...state.entities,
      [entity.id]: entity,
    },
    ids: [...state.ids, entity.id],
  };
}

